import axios, { AxiosResponse } from 'axios';
import axiosInstance from './axiosInstance';
import { finishLoading } from '../connected-components/loading/actions';
import { tr } from '../translation';
import { toast } from 'sonner';

/**
 * Handles API errors in a consistent way
 * @param {any} err - The error object returned from API requests
 * @param {boolean} [hideGlobalErrorToast] - Whether to suppress the error toast notification
 */
const handleError = (err: any, hideGlobalErrorToast?: boolean) => {
  finishLoading();
  console.warn(err);
  const message = err?.response?.data?.message
    ? err?.response?.data?.message
    : err?.response?.data?.error?.message
      ? err?.response?.data?.error?.message
      : tr('app.error');

  if (!hideGlobalErrorToast) {
    toast.error(message);
  }
};

/**
 * HTTP request utility methods for API communication
 * Provides standardized methods for API requests with error handling
 */
const http = {
  /**
   * Performs a GET request
   * @param {string} url - The API endpoint URL
   * @param {boolean} [hideGlobalErrorToast] - Whether to suppress error toast notifications
   * @returns {Promise<any>} The API response data
   */
  get: (url: string, hideGlobalErrorToast?: boolean) =>
    new Promise((resolve, reject) => {
      axiosInstance
        .get(url)
        .then((res: AxiosResponse<any>) => {
          resolve(res?.data);
        })
        .catch((err) => {
          handleError(err, hideGlobalErrorToast);
          reject(err);
        });
    }),
  /**
   * Performs a POST request
   * @param {string} url - The API endpoint URL
   * @param {any} body - The request body data
   * @param {object} [headers={}] - Additional request headers
   * @param {boolean} [hideGlobalErrorToast] - Whether to suppress error toast notifications
   * @returns {Promise<any>} The API response data
   */
  post: (url: string, body: any, headers = {}, hideGlobalErrorToast?: boolean) =>
    new Promise((resolve, reject) => {
      axiosInstance
        .post(url, body, { headers })
        .then((res) => {
          if (res?.data?.code !== 200) {
            handleError(res, hideGlobalErrorToast);
          }
          resolve(res?.data);
        })
        .catch((err) => {
          handleError(err, hideGlobalErrorToast);
          reject(err);
        });
    }),
  /**
   * Performs a POST request with streaming response
   * @param {string} url - The API endpoint URL
   * @param {any} body - The request body data
   * @param {object} [headers={}] - Additional request headers
   * @param {boolean} [hideGlobalErrorToast] - Whether to suppress error toast notifications
   * @returns {Promise<any>} The streaming API response data
   */
  postStreaming: (url: string, body: any, headers = {}, hideGlobalErrorToast?: boolean) =>
    new Promise((resolve, reject) => {
      axios
        .post(url, JSON.stringify(body), {
          headers: {
            ...headers,
            'Content-Type': 'application/json',
            Authorization: 'Basic YXBwOkRFV1IkIyUj',
          },
          baseURL: 'https://canguru.dev/',
          responseType: 'stream',
          maxBodyLength: Infinity,
          maxContentLength: Infinity,
        })
        .then((res) => {
          if (res?.data?.code !== 200) {
            handleError(res, hideGlobalErrorToast);
          }
          resolve(res?.data);
        })
        .catch((err) => {
          handleError(err, hideGlobalErrorToast);
          reject(err);
        });
    }),
  /**
   * Performs a PUT request
   * @param {string} url - The API endpoint URL
   * @param {any} body - The request body data
   * @param {object} [headers={}] - Additional request headers
   * @param {boolean} [hideGlobalErrorToast] - Whether to suppress error toast notifications
   * @returns {Promise<any>} The API response data
   */
  put: (url: string, body: any, headers = {}, hideGlobalErrorToast?: boolean) =>
    new Promise((resolve, reject) => {
      axiosInstance
        .put(url, body, { headers })
        .then((res) => {
          if (res?.data?.code !== 200) {
            handleError(res, hideGlobalErrorToast);
          }
          resolve(res?.data);
        })
        .catch((err) => {
          handleError(err, hideGlobalErrorToast);
          reject(err);
        });
    }),
  /**
   * Performs a DELETE request
   * @param {string} url - The API endpoint URL
   * @param {boolean} [hideGlobalErrorToast] - Whether to suppress error toast notifications
   * @returns {Promise<any>} The API response data
   */
  delete: (url: string, hideGlobalErrorToast?: boolean) =>
    new Promise((resolve, reject) => {
      axiosInstance
        .delete(url)
        .then((res) => {
          if (res?.data?.code !== 200) {
            handleError(res, hideGlobalErrorToast);
          }
          resolve(res?.data);
        })
        .catch((err) => {
          handleError(err, hideGlobalErrorToast);
          reject(err);
        });
    }),
};

export default http;
